'use client'

import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut/useKeyboardShortcut'
import { Autocomplete, IconButton, InputAdornment, TextField, Tooltip, type AutocompleteInputChangeReason } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { KeyboardEvent, startTransition, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiCornerDownLeft, FiSearch, FiX } from 'react-icons/fi'

export interface SearchInputProps {
    value: string
    onChange: (value: string) => void
    onSubmit: (value: string) => void
    onClear: () => void
    placeholder?: string
    fullWidth?: boolean
    disabled?: boolean
}

/** Phones and tablets: no keyboard to press ⌘K on, so the hint isn't shown and its room goes to typing.
 * `pointer-coarse:hidden` on the hint covers the first paint, before useMediaQuery has read the query. */
const TOUCH_SCREEN_QUERY = '(pointer: coarse)'

const ShortcutHint = ({ isApple }: { isApple: boolean }) => (
    <Tooltip arrow title={isApple ? 'Use command + K to search' : 'Use ctrl + K to search'}>
        <span className="inline-flex items-center gap-1 select-none cursor-default pointer-coarse:hidden" aria-hidden>
            <kbd
                className={clsx(
                    'inline-flex items-center justify-center',
                    'h-5.5 min-w-5.5 rounded-md',
                    'font-medium text-foreground-muted bg-black/5 dark:bg-white/10',
                    isApple ? 'text-base' : 'text-[10px] px-1.5'
                )}
            >
                {isApple ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd className="inline-flex h-5.5 min-w-5.5 items-center justify-center rounded-md bg-black/5 text-[11px] font-medium text-foreground-muted dark:bg-white/10">
                K
            </kbd>
        </span>
    </Tooltip>
)

/** Shown once there's a value — the search only runs on Enter, so say so. */
const EnterHint = () => (
    <Tooltip arrow title="Press Enter to search">
        <kbd
            className="inline-flex h-5.5 min-w-5.5 cursor-default items-center justify-center rounded-md bg-black/5 text-sm font-medium text-foreground-muted select-none dark:bg-white/10"
            aria-hidden
            data-testid="search-input/enter-hint"
        >
            <FiCornerDownLeft className="text-[10px]!" />
        </kbd>
    </Tooltip>
)

/** 1 = hint → clear (forward), -1 = clear → hint (reverse) */
const endAdornmentVariants = {
    initial: (direction: number) => ({
        opacity: 0,
        x: 6 * direction,
        filter: 'blur(2px)',
    }),
    animate: {
        opacity: 1,
        x: 0,
        filter: 'blur(0px)',
    },
    exit: (direction: number) => ({
        opacity: 0,
        x: -6 * direction,
        filter: 'blur(2px)',
    }),
}

const endAdornmentTransition = { duration: 0.16, ease: 'easeOut' as const }

export const SearchInput = ({ value, onChange, onSubmit, onClear, placeholder = 'Search', fullWidth, disabled }: SearchInputProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null)

    // INTENTIONAL: the input owns its text (`draft`) so typing never waits on the parent. Changes go up as a
    // transition (a slow parent re-render can't block keystrokes); the parent's `value` is taken back only when
    // the parent changes it itself (clear, URL restore) — not when it echoes what we just emitted.
    const [draft, setDraft] = useState(value)
    const lastEmittedRef = useRef(value)
    useEffect(() => {
        if (value === lastEmittedRef.current) return
        lastEmittedRef.current = value
        setDraft(value)
    }, [value])

    const hasValue = Boolean(draft)
    const adornmentDirection = hasValue ? 1 : -1
    // The Enter hint only shows while the text differs from what was last searched;
    // a value present on mount (e.g. restored from the URL) counts as already searched.
    const [lastSubmittedValue, setLastSubmittedValue] = useState(() => value.trim())
    const showEnterHint = hasValue && draft.trim() !== lastSubmittedValue

    const isTouchScreen = useMediaQuery(TOUCH_SCREEN_QUERY)
    const isApple = useMemo(() => {
        if (typeof navigator === 'undefined') return true
        return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) || navigator.userAgent.includes('Mac')
    }, [])

    const shortcutOptions = useMemo(() => ({ ...(isApple ? { metaKey: true } : { ctrlKey: true }), enabled: !disabled }), [isApple, disabled])

    const focusSearch = useCallback(() => {
        const input = inputRef.current
        if (!input || disabled) return
        // Defer so we run after the keydown finishes (avoids Autocomplete swallowing focus).
        requestAnimationFrame(() => {
            input.focus()
            input.select()
        })
    }, [disabled])

    useKeyboardShortcut('K', focusSearch, shortcutOptions)

    // Clearing also clears the applied search, so nothing is "last searched" any more.
    const handleClear = () => {
        setDraft('')
        lastEmittedRef.current = ''
        setLastSubmittedValue('')
        onClear()
    }

    const handleInputChange = (_event: SyntheticEvent, nextValue: string, reason: AutocompleteInputChangeReason) => {
        // Only real typing; Autocomplete's own 'reset'/'clear' echoes would fight the draft.
        if (reason !== 'input') return
        setDraft(nextValue)
        lastEmittedRef.current = nextValue
        startTransition(() => onChange(nextValue))
        if (nextValue === '') handleClear()
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const nextValue = event.currentTarget?.value?.trim() || (event.target as HTMLInputElement)?.value?.trim() || ''
            setLastSubmittedValue(nextValue)
            onSubmit(nextValue)
        }
    }

    return (
        <div className={clsx('flex items-center gap-1', fullWidth ? 'w-full min-w-0' : 'shrink-0')}>
            <div className={clsx('w-full', !fullWidth && 'min-w-xs max-w-xs')}>
                <Autocomplete
                    freeSolo
                    fullWidth
                    options={[]}
                    disableClearable
                    size="small"
                    disabled={disabled}
                    inputValue={draft}
                    onInputChange={handleInputChange}
                    renderInput={(params) => {
                        const { ref: paramsRef, ...inputProps } = params.inputProps

                        return (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'var(--border-color)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'var(--border-color-focus)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'var(--border-color-focus)',
                                        },
                                    },
                                }}
                                onKeyDown={handleKeyDown}
                                slotProps={{
                                    htmlInput: {
                                        ...inputProps,
                                        type: 'text',
                                        'data-testid': 'search-input',
                                        ref: (node: HTMLInputElement | null) => {
                                            inputRef.current = node
                                            if (typeof paramsRef === 'function') {
                                                paramsRef(node)
                                            } else if (paramsRef) {
                                                ;(paramsRef as React.MutableRefObject<HTMLInputElement | null>).current = node
                                            }
                                        },
                                    },
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FiSearch className="ml-2! text-base!" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: disabled ? undefined : (
                                            <InputAdornment position="end" className="mr-0.5! relative min-w-12 justify-end! pointer-coarse:min-w-0!">
                                                <AnimatePresence mode="wait" initial={false} custom={adornmentDirection}>
                                                    {hasValue ? (
                                                        <motion.div
                                                            key="clear"
                                                            className="flex items-center justify-center"
                                                            custom={adornmentDirection}
                                                            variants={endAdornmentVariants}
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={endAdornmentTransition}
                                                        >
                                                            <IconButton
                                                                aria-label="Clear search"
                                                                onClick={handleClear}
                                                                size="small"
                                                                className="border-none! font-medium!"
                                                            >
                                                                <FiX className="text-sm! text-inherit!" />
                                                            </IconButton>
                                                            <AnimatePresence initial={false}>
                                                                {showEnterHint && (
                                                                    // Width animates with the fade (clipped), so the clear button slides over
                                                                    // smoothly and the hint never leaves the input while it exits.
                                                                    <motion.span
                                                                        key="enter-hint"
                                                                        className="flex justify-end overflow-hidden"
                                                                        initial={{ width: 0, opacity: 0 }}
                                                                        animate={{ width: 'auto', opacity: 1 }}
                                                                        exit={{ width: 0, opacity: 0 }}
                                                                        transition={endAdornmentTransition}
                                                                    >
                                                                        <span className="flex shrink-0 pl-0.5">
                                                                            <EnterHint />
                                                                        </span>
                                                                    </motion.span>
                                                                )}
                                                            </AnimatePresence>
                                                        </motion.div>
                                                    ) : isTouchScreen ? null : (
                                                        <motion.div
                                                            key="shortcut"
                                                            className="flex items-center -mb-px"
                                                            custom={adornmentDirection}
                                                            variants={endAdornmentVariants}
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={endAdornmentTransition}
                                                        >
                                                            <ShortcutHint isApple={isApple} />
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            height: '40px',
                                        },
                                    },
                                }}
                            />
                        )
                    }}
                />
            </div>
        </div>
    )
}
